import type {ReactElement} from 'react';
import React from 'react';
import type {GoogleSignInCallback} from '@Components/login-page/login-page.types';
import {noop} from '@Utils/general.util';
import {Text, TextSize} from '@Components/text';
import {openErrorModal} from '@Modals/error-modal';
import {clearModalHistoryEntry} from '@Components/modal-container';
import {Image} from 'react-bootstrap';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import {ButtonSize} from '@Components/button';
import {AuthSsoButton} from '@Components/auth-sso-button/auth-sso-button';
import {useGoogleSignIn} from '@Hooks/login-signup/useGoogleSignIn';
import styles from './google-login-button.module.scss';
import {getAssetUrl} from '@Utils/s3.util';

interface GoogleLoginButtonProps {
  onSignInDoneCallback?: GoogleSignInCallback;
  hideButton?: boolean;
  isSignup?: boolean;
  redirectUrl?: string;
  containerClassname?: string;
  isConnect?: boolean;
  isDisconnect?: boolean;
  /** Forwarded to the shared SSO shell; the widescreen login page draws these larger. */
  size?: ButtonSize;
  /** The two-column reflow leaves room for the provider name alone, not the full call to action. */
  showProviderNameOnly?: boolean;
}

function GoogleLoginButton({showProviderNameOnly = false, onSignInDoneCallback = noop, hideButton = false, isSignup = false, redirectUrl = '', containerClassname, size = ButtonSize.MEDIUM, ...props}: GoogleLoginButtonProps): ReactElement<any> {
  const {signInWithGoogle, connectGoogleAccount} = useGoogleSignIn({redirectUrl, onSignInDone: onSignInDoneCallback});

  const getContainerClassName = (): string => {
    if (containerClassname) {
      return containerClassname;
    }
    return styles.buttonContainer;
  };

  const disconnectGoogleAccount = (): void => {
    clearModalHistoryEntry();
    void window.PMW.write(window.PMW.util.site_url('user/disconnectGoogleAccount'), null, (response: AjaxSuccessResponse) => {
      if (response.status === 'success') {
        window.location.href = window.PMW.util.site_url('user/editprofile');
      } else {
        openErrorModal({message: window.i18next.t('pmwjs_error_processing_request_try_again')});
      }
    });
  };

  const onDisconnectGoogleAccount = async (): Promise<void> => {
    const response = await window.PMW.readLocal('authenticate/isEmailAccountUser');
    if (!('isEmailAccountUser' in response) || !('is2FAEnabled' in response)) {
      openErrorModal({message: window.i18next.t('pmwjs_error_processing_request_try_again')});
      return;
    }

    if (response.isEmailAccountUser) {
      window.PMW.openPasswordVerificationModal({
        onSuccessCallback: onDisconnectPasswordVerification,
        check2FAStatus: true
      });
    } else if (response.is2FAEnabled) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: disconnectGoogleAccount,
        onlyTOTPValid: true
      });
    } else {
      disconnectGoogleAccount();
    }
  }

  const onDisconnectPasswordVerification = (is2FAPending = false): void => {
    if (is2FAPending) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: disconnectGoogleAccount,
        onlyTOTPValid: true
      });
    } else {
      disconnectGoogleAccount();
    }
  }

  const onConnectGoogleAccount = async (): Promise<void> => {
    const response = await window.PMW.readLocal('authenticate/isEmailAccountUser');
    if (!('isEmailAccountUser' in response) || !('is2FAEnabled' in response)) {
      openErrorModal({message: window.i18next.t('pmwjs_error_processing_request_try_again')});
      return;
    }

    if (response.isEmailAccountUser) {
      window.PMW.openPasswordVerificationModal({
        onSuccessCallback: onConnectPasswordVerification,
        check2FAStatus: true
      });
    } else if (response.is2FAEnabled) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: connectGoogleAccount,
        onlyTOTPValid: true
      });
    } else {
      void connectGoogleAccount();
    }
  }

  const onConnectPasswordVerification = (is2FAPending = false): void => {
    if (is2FAPending) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: connectGoogleAccount,
        onlyTOTPValid: true
      });
    } else {
      void connectGoogleAccount();
    }
  }

  if (props.isDisconnect) {
    return <Text val={window.i18next.t('pmwjs_disconnect')} size={TextSize.MD} onClick={(): void => {
      void onDisconnectGoogleAccount();
    }} />;
  }

  if (props.isConnect) {
    return (
      <Text
        val={window.i18next.t('pmwjs_connect')}
        size={TextSize.MD}
        onClick={(): void => {
          void onConnectGoogleAccount();
        }}
      />
    );
  }

  const getButtonText = (): string => {
    if (showProviderNameOnly) {
      return window.i18next.t('pmwjs_google');
    }

    return isSignup ? window.i18next.t('pmwjs_sign_in_with_google') : window.i18next.t('pmwjs_log_in_with_google');
  };

  const getGoogleLogo = (): ReactElement => {
    return <Image alt="" className={styles.brandMark} src={getAssetUrl('login-page-google-icon.svg')} />;
  };

  const getButton = (): ReactElement | null => {
    if (hideButton) {
      return null;
    }
    return <AuthSsoButton customIcon={getGoogleLogo()} onClick={signInWithGoogle} size={size} text={getButtonText()} />;
  };

  return <div className={getContainerClassName()}>{getButton()}</div>;
}

export default React.memo(GoogleLoginButton);
